import type { IntlShape } from 'react-intl';

import { ErrorKeySet, type ValidationError, type Validator } from '../../../types/field-validation';
import type { FormFieldValidation, FormOption, OptionId } from '../../../types/form/form-fields';
import type { OptionsValue } from '../../../types/form/form-fields/field-values';
import { getFailedRequiredValidationError, getFailedValidationError } from '../utils';
import { messages } from './messages';

const optionIsSelected = (selectedOptionIds: OptionId[], optionId: OptionId) =>
	selectedOptionIds.includes(optionId) ||
	selectedOptionIds.map((id) => id.split(':')[0]).includes(optionId.toString());

export const getOptionValidationErrors = (
	options: FormOption[],
	validation: FormFieldValidation,
	fieldLabel: string,
	intl?: IntlShape,
	option?: OptionsValue,
): ReturnType<Validator> => {
	if (Object.keys(validation).length === 0) {
		return [];
	}
	if (!option) {
		if (validation.required) {
			return [getFailedRequiredValidationError(fieldLabel, intl)];
		}
		return [];
	}

	const numOptionsSelected =
		(option.options?.length ?? 0) + (option.otherOptionText !== undefined ? 1 : 0);
	const validationErrors: ValidationError[] = [];

	// Validate required
	if (validation.required) {
		if (numOptionsSelected === 0) {
			validationErrors.push(getFailedRequiredValidationError(fieldLabel, intl));
		}
	}

	if (!validation.required && numOptionsSelected === 0) {
		return [] as ValidationError[];
	}

	if (
		validation.minimumOptionSelections &&
		validation.minimumOptionSelections === validation.maximumOptionSelections
	) {
		// Check for exact num of options
		if (numOptionsSelected !== validation.minimumOptionSelections) {
			validationErrors.push(
				getFailedValidationError(
					ErrorKeySet.INVALID,
					intl?.formatMessage(messages.exactOptions, {
						minimum: validation.minimumOptionSelections,
					}),
				),
			);
		}
	} else if (validation.minimumOptionSelections && validation.maximumOptionSelections) {
		// check for in between num of options
		if (
			numOptionsSelected < validation.minimumOptionSelections ||
			numOptionsSelected > validation.maximumOptionSelections
		) {
			validationErrors.push(
				getFailedValidationError(
					ErrorKeySet.INVALID,
					intl?.formatMessage(messages.betweenOptions, {
						minimum: validation.minimumOptionSelections,
						maximum: validation.maximumOptionSelections,
					}),
				),
			);
		}
	} else {
		// min or max num of options
		if (
			validation.minimumOptionSelections &&
			numOptionsSelected < validation.minimumOptionSelections
		) {
			validationErrors.push(
				getFailedValidationError(
					ErrorKeySet.INVALID,
					intl?.formatMessage(messages.minimumOptions, {
						minimum: validation.minimumOptionSelections,
					}),
				),
			);
		}

		if (
			validation.maximumOptionSelections &&
			numOptionsSelected > validation.maximumOptionSelections
		) {
			validationErrors.push(
				getFailedValidationError(
					ErrorKeySet.INVALID,
					intl?.formatMessage(messages.maximumOptions, {
						maximum: validation.maximumOptionSelections,
					}),
				),
			);
		}
	}

	const requiredOptionId = validation.requiredOption?.id;

	if (requiredOptionId) {
		if (
			!optionIsSelected(
				option.options.map((c) => c.id),
				requiredOptionId,
			)
		) {
			validationErrors.push(
				getFailedValidationError(
					ErrorKeySet.REQUIRED,
					intl?.formatMessage(messages.requiredOption, {
						label: validation.requiredOption?.label ?? requiredOptionId,
					}),
				),
			);
		}
	}

	return validationErrors;
};
